import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Form, Spinner, Badge } from 'react-bootstrap';
import authService from '../services/authService';
import BackToDashboard from '../components/BackToDashboard';

function LeadMagnetLanding() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState('generate'); // generate, generating, editing
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Landing page data
  const [landingPageCopy, setLandingPageCopy] = useState('');
  const [savedLandingPage, setSavedLandingPage] = useState(null);
  const [savedTopic, setSavedTopic] = useState(null);
  const [targetMarket, setTargetMarket] = useState('');

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Get target market for personalized title
    try {
      const marketResponse = await fetch('/api/target-market/primary', {
        credentials: 'include'
      });

      if (marketResponse.ok) {
        const marketData = await marketResponse.json();
        if (marketData.primary_niche) {
          setTargetMarket(marketData.primary_niche.niche_name || '');
        }
      }
    } catch (err) {
      console.error('Error loading target market:', err);
    }

    // Check prerequisites
    try {
      const topicResponse = await fetch('/api/assets/lead-magnet/get-topic', {
        credentials: 'include'
      });

      if (topicResponse.ok) {
        const topicData = await topicResponse.json();
        if (!topicData.topic) {
          navigate('/lead-magnet-topic');
          return;
        }
        setSavedTopic(topicData.topic);
      } else {
        navigate('/lead-magnet-topic');
        return;
      }

      const contentResponse = await fetch('/api/assets/lead-magnet/get-content', {
        credentials: 'include'
      });

      if (contentResponse.ok) {
        const contentData = await contentResponse.json();
        if (!contentData.content) {
          navigate('/lead-magnet-content');
          return;
        }
      } else {
        navigate('/lead-magnet-content');
        return;
      }

      // Check if landing page is saved
      const landingResponse = await fetch('/api/assets/lead-magnet/get-landing-page', {
        credentials: 'include'
      });

      if (landingResponse.ok) {
        const landingData = await landingResponse.json();
        if (landingData.landing_page) {
          setSavedLandingPage(landingData.landing_page);
          setLandingPageCopy(landingData.landing_page);
          setStage('editing');
        }
      }
    } catch (err) {
      console.error('Error loading saved data:', err);
    }

    setLoading(false);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    setStage('generating');

    try {
      const response = await fetch('/api/assets/lead-magnet/generate-landing-page', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setLandingPageCopy(data.landing_page);
        setStage('editing');
      } else {
        const errorData = await response.json();
        setError(`Failed to generate landing page: ${errorData.error || 'Unknown error'}`);
        setStage('generate');
      }
    } catch (err) {
      setError('Error generating landing page: ' + err.message);
      setStage('generate');
    }

    setGenerating(false);
  };

  const handleSave = async () => {
    if (!landingPageCopy.trim()) {
      setError('Please enter your landing page copy');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const response = await fetch('/api/assets/lead-magnet/save-landing-page', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ landing_page: landingPageCopy })
      });

      if (response.ok) {
        setSavedLandingPage(landingPageCopy);
        setError('');
        alert('Landing page copy saved successfully!');
      } else {
        const errorData = await response.json();
        setError(`Failed to save landing page: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      setError('Error saving landing page: ' + err.message);
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <BackToDashboard module="assets-creation" />
      <Row className="mb-4">
        <Col>
          <h1>Lead Magnet Landing Page{targetMarket && ` for ${targetMarket}`}</h1>
          <p className="text-muted">
            Create high-converting landing page copy for your lead magnet.
          </p>
          {savedTopic && (
            <Alert variant="info">
              <strong>Lead Magnet:</strong> {savedTopic.topic} <Badge bg="secondary">{savedTopic.format}</Badge>
            </Alert>
          )}
        </Col>
      </Row>

      {error && !error.includes('saved successfully') && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {/* Generate Stage */}
      {stage === 'generate' && (
        <Row>
          <Col md={8}>
            <Card>
              <Card.Body>
                <h4 className="mb-4">📄 Generate Landing Page Copy</h4>
                <p className="mb-4">
                  Our AI will create compelling landing page copy based on your lead magnet topic and content outline.
                </p>

                <Alert variant="info" className="mb-4">
                  <strong>What you'll get:</strong>
                  <ul className="mb-0 mt-2">
                    <li>Attention-grabbing headline and subheadline</li>
                    <li>5-7 benefit-driven bullet points</li>
                    <li>Social proof suggestions</li>
                    <li>Compelling call-to-action copy</li>
                    <li>Visual design suggestions</li>
                  </ul>
                </Alert>

                <div className="d-grid">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleGenerate}
                    disabled={generating}
                  >
                    {generating ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Generating Landing Page Copy... (30-45 seconds)
                      </>
                    ) : (
                      'Generate Landing Page Copy →'
                    )}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            <Card className="bg-light">
              <Card.Body>
                <h5 className="mb-3">🎯 Landing Page Tips</h5>
                <ul className="small mb-0">
                  <li>Lead with benefits, not features</li>
                  <li>Create urgency without being pushy</li>
                  <li>Address objections proactively</li>
                  <li>Make the CTA button text specific</li>
                  <li>Use social proof to build trust</li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Generating Stage */}
      {stage === 'generating' && (
        <Row>
          <Col md={12}>
            <Card>
              <Card.Body className="text-center py-5">
                <Spinner animation="border" className="mb-3" />
                <h4>Generating Your Landing Page Copy...</h4>
                <p className="text-muted">
                  This will take about 30-45 seconds. We're creating conversion-optimized copy for your landing page.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Editing Stage */}
      {stage === 'editing' && (
        <>
          <Row className="mb-4">
            <Col md={12}>
              <Card>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">✏️ Review & Edit Landing Page Copy</h4>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleGenerate}
                      disabled={generating}
                    >
                      {generating ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Regenerating...
                        </>
                      ) : (
                        'Regenerate Copy'
                      )}
                    </Button>
                  </div>

                  <Alert variant="info" className="mb-4">
                    <strong>Review the AI-generated landing page copy below.</strong> You can edit it directly or regenerate a new version.
                  </Alert>

                  <Form.Group className="mb-4">
                    <Form.Label><strong>Landing Page Copy:</strong></Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={30}
                      value={landingPageCopy}
                      onChange={(e) => setLandingPageCopy(e.target.value)}
                      style={{ fontFamily: 'monospace', fontSize: '14px' }}
                    />
                    <Form.Text className="text-muted">
                      This copy will be used for your landing page. Make any adjustments you'd like.
                    </Form.Text>
                  </Form.Group>

                  <div className="d-grid gap-2">
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={handleSave}
                      disabled={saving || !landingPageCopy.trim()}
                    >
                      {saving ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : (
                        'Save Landing Page Copy'
                      )}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {savedLandingPage && (
            <Row>
              <Col md={12}>
                <Card className="border-success">
                  <Card.Body className="text-center">
                    <h4 className="text-success mb-3">✓ Lead Magnet Complete!</h4>
                    <p className="text-muted mb-4">
                      Your lead magnet strategy is complete. You have:
                    </p>
                    <ul className="text-start mb-4" style={{ maxWidth: '500px', margin: '0 auto' }}>
                      <li>Selected topic: <strong>{savedTopic?.topic}</strong></li>
                      <li>Detailed content outline</li>
                      <li>Landing page copy ready to use</li>
                    </ul>
                    <div className="d-flex gap-3 justify-content-center flex-wrap">
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={() => navigate('/email-sequences')}
                      >
                        Continue to Email Sequences →
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={() => navigate('/dashboard/assets-creation')}
                      >
                        Return to Dashboard
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={() => navigate('/lead-magnet-topic')}
                      >
                        Replace this Lead Magnet
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </>
      )}
    </Container>
  );
}

export default LeadMagnetLanding;
